'use client';

import React, { useState } from 'react';

import { Rating } from '@bytel/product-wall';
import {
    Column,
    Columns,
    Divider,
    Icon,
    IconColor,
    IconName,
    IconSize,
    Link,
    Spacer,
    SpacerSize,
    Text,
    TextLevels,
    TextMarkup,
    Title,
    TitleLevels,
    TypographyBold,
    TypographyTransform,
    View,
} from '@bytel/trilogy-react';

import { ReviewType } from '@app-types/review';

export type UserReviewProps = {
    review: ReviewType;
};

export function UserReview({ review }: UserReviewProps) {
    const {
        user: { login, gender, ageLabel, isVerified },
        title,
        note,
        created_at,
        isRecommended,
        message,
        shortMessage,
        pros,
        cons,
        recommendedFor,
    } = review;

    const [canShowMore, setCanShowMore] = useState<boolean>(message.length > shortMessage.length);

    const sinceTime = new Date().getTime() - new Date(created_at).getTime();
    const sinceHours = Math.round(sinceTime / (1000 * 3600));
    const since = sinceHours < 24 ? `${sinceHours} heures` : `${Math.round(sinceHours / 24)} jours`;

    return (
        <>
            <Columns>
                <Column size={3} className='has-text-centered'>
                    <Icon name={IconName.USERS} size={IconSize.LARGE} />
                    <Text typo={TypographyBold.TEXT_WEIGHT_SEMIBOLD} marginless>
                        {login}
                    </Text>
                    {gender && <Text marginless>{gender}</Text>}
                    {ageLabel && <Text marginless>{ageLabel}</Text>}
                    {isVerified && (
                        <Text className='is-flex is-aligned-center is-justified-center'>
                            <Icon name={IconName.CHECK_CIRCLE} color={IconColor.SUCCESS} />
                            &nbsp;Acheteur vérifié
                        </Text>
                    )}
                </Column>
                <Column>
                    <View flexable>
                        <Rating rating={note * 2} />
                    </View>
                    <Title level={TitleLevels.FIVE} typo={TypographyBold.TEXT_WEIGHT_NORMAL}>
                        {title}
                    </Title>
                    <Text
                        level={TextLevels.TWO}
                        typo={[TypographyTransform.TEXT_UPPERCASE, TypographyBold.TEXT_WEIGHT_SEMIBOLD]}
                    >
                        Il y a {since}
                    </Text>
                    {isRecommended ? (
                        <Text className='is-flex'>
                            <Icon name={IconName.CHECK_CIRCLE} color={IconColor.SUCCESS} />
                            &nbsp;Oui, je recommande ce produit
                        </Text>
                    ) : (
                        <Text className='is-flex'>
                            <Icon name={IconName.TIMES_CIRCLE} color={IconColor.ERROR} />
                            &nbsp;Non, je ne recommande pas ce produit
                        </Text>
                    )}
                    <Spacer size={SpacerSize.TWO} />
                    <Text level={TextLevels.TWO}>{canShowMore ? `${shortMessage} ...` : message}</Text>
                    {canShowMore && <Link onClick={() => setCanShowMore(false)}>En savoir plus</Link>}
                    {pros && (
                        <Text level={TextLevels.TWO}>
                            <Text markup={TextMarkup.SPAN} typo={TypographyBold.TEXT_WEIGHT_SEMIBOLD}>
                                Points forts&nbsp;:&nbsp;
                            </Text>
                            <Text markup={TextMarkup.SPAN}>{pros}</Text>
                        </Text>
                    )}
                    {cons && (
                        <Text level={TextLevels.TWO}>
                            <Text markup={TextMarkup.SPAN} typo={TypographyBold.TEXT_WEIGHT_SEMIBOLD}>
                                Points faibles :&nbsp;
                            </Text>
                            <Text markup={TextMarkup.SPAN}>{cons}</Text>
                        </Text>
                    )}
                    {recommendedFor && (
                        <Text level={TextLevels.TWO}>
                            <Text markup={TextMarkup.SPAN} typo={TypographyBold.TEXT_WEIGHT_SEMIBOLD}>
                                Je recommande ce produit pour&nbsp;:
                            </Text>
                            <Text markup={TextMarkup.SPAN}> {recommendedFor}</Text>
                        </Text>
                    )}
                </Column>
            </Columns>
            <Divider />
        </>
    );
}
